import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { Check, CheckCheck, CreditCard, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { buildWhatsAppUrl } from "@/lib/phone";

/** Shared with the header bell so "read" state stays in sync. */
export const SEEN_PAYMENTS_KEY = "admin:seen-payments";
export const SEEN_PAYMENTS_EVENT = "admin:seen-payments-changed";

export function readSeenPayments(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(SEEN_PAYMENTS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function writeSeenPayments(ids: Set<string>) {
  try {
    // keep the list bounded
    const arr = [...ids].slice(-500);
    window.localStorage.setItem(SEEN_PAYMENTS_KEY, JSON.stringify(arr));
    window.dispatchEvent(new Event(SEEN_PAYMENTS_EVENT));
  } catch {
    /* ignore */
  }
}

type Row = {
  id: string;
  buyer_name: string;
  buyer_phone: string | null;
  plan_name: string;
  amount_paise: number;
  created_at: string;
};

function planTone(plan: string) {
  const p = plan.toLowerCase();
  if (p.includes("refusal")) return "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200";
  if (p.includes("basic")) return "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200";
  return "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200";
}

export function RecentPaymentsWidget({ compact = false }: { compact?: boolean } = {}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("payments")
      .select("id, buyer_name, buyer_phone, plan_name, amount_paise, created_at")
      .eq("status", "paid")
      .order("created_at", { ascending: false })
      .limit(15);
    setRows((data ?? []) as Row[]);
  }, []);

  useEffect(() => {
    setSeen(readSeenPayments());
    const sync = () => setSeen(readSeenPayments());
    window.addEventListener(SEEN_PAYMENTS_EVENT, sync);
    window.addEventListener("storage", sync);
    void load();
    const t = setInterval(load, 20_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener(SEEN_PAYMENTS_EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const markRead = (ids: string[]) => {
    const next = new Set(seen);
    ids.forEach((id) => next.add(id));
    setSeen(next);
    writeSeenPayments(next);
  };

  const all = rows ?? [];
  const unread = all.filter((r) => !seen.has(r.id));
  const visible = showAll ? all : unread;

  return (
    <div className={`flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-foreground">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          {!compact && <CreditCard className="h-4 w-4 text-primary" />} Live Recent Payments
          {unread.length > 0 && (
            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-black text-primary-foreground">
              {unread.length} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="rounded-lg border border-border px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-muted"
          >
            {showAll ? "Unread" : "All"}
          </button>
          <button
            type="button"
            disabled={unread.length === 0}
            onClick={() => markRead(all.map((r) => r.id))}
            title="Mark all as read"
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground disabled:opacity-40"
          >
            <CheckCheck className="h-3.5 w-3.5" /> {compact ? "Read" : "Mark all as read"}
          </button>
        </div>
      </div>

      <div className={`mt-2 overflow-y-auto ${compact ? "max-h-56 flex-1" : "max-h-96"}`}>
        {rows === null && <p className="py-6 text-center text-xs text-muted-foreground">Loading payments…</p>}
        {rows !== null && visible.length === 0 && (
          <p className="py-6 text-center text-xs text-muted-foreground">
            {showAll ? "No payments yet." : "All caught up — no new payments."}
          </p>
        )}
        {visible.map((p) => {
          const isNew = !seen.has(p.id);
          const wa = p.buyer_phone ? buildWhatsAppUrl(p.buyer_phone, `Hi ${p.buyer_name}, welcome to WeLivdIt Visa Prep!`) : null;
          return (
            <div
              key={p.id}
              className={`flex items-center gap-3 border-b border-border/40 py-2.5 last:border-b-0 ${isNew ? "" : "opacity-60"}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link
                    to="/admin/students"
                    className="truncate text-sm font-semibold text-foreground hover:underline"
                  >
                    {p.buyer_name}
                  </Link>
                  {isNew && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${planTone(p.plan_name)}`}>
                    {p.plan_name}
                  </span>
                  <span className="font-semibold text-foreground">
                    ₹{Math.round((p.amount_paise || 0) / 100).toLocaleString("en-IN")}
                  </span>
                  <span>· {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}</span>
                </div>
              </div>
              {wa && (
                <a
                  href={wa}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`WhatsApp ${p.buyer_name}`}
                  className="grid h-8 w-8 place-items-center rounded-lg border border-border text-emerald-600 hover:bg-muted"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              )}
              {isNew && (
                <button
                  type="button"
                  onClick={() => markRead([p.id])}
                  aria-label="Mark as read"
                  className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
                >
                  <Check className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
